import React from 'react';
import styled from 'styled-components';
import linkifyHtml from 'linkify-html';
import {useMailCampaignQuery} from 'api';
import {Quill as ReactQuill} from 'components/Ui';
import {useParams} from 'react-router-dom';
import Code from './Code';

interface Props {
  value: string;
  onChange: (textHtml: string, plainText?: string) => void;
  modules: any;
}

export default ({value, onChange, modules}: Props) => {
  const ref = React.useRef(null);

  const {mailCampaignId} = useParams<{mailCampaignId: string}>();

  const {data: {mailCampaign = null} = {}} = useMailCampaignQuery({
    variables: {uuid: mailCampaignId},
    skip: !mailCampaignId,
  });

  const convertUrlToLink = (text: string) =>
    linkifyHtml(text, {
      defaultProtocol: 'https',
      rel: 'noopener noreferrer',
      target: '_blank',
    });

  const hasFontSetting = !!modules?.toolbar?.container
    ?.flat(Infinity)
    .some((item: any) => item && typeof item === 'object' && 'font' in item);

  return (
    <Container>
      <div id="toolbar">
        {hasFontSetting && (
          <select className="ql-font">
            <option value="p_gothic">Pゴシック</option>
            <option value="p_mincho">P明朝</option>
            <option value="ms_gothic">ゴシック</option>
            <option value="arial">Arial</option>
            <option value="times_new_roman">Times New Roman</option>
            <option value="monospace">monospace</option>
            <option value="arial_black">Arial Black</option>
            <option value="arial_narrow">Arial Narrow</option>
            <option value="comic_sans_ms">Comic Sans</option>
            <option value="garamond">Garamond</option>
            <option value="georgia">Georgia</option>
            <option value="tahoma">Tahoma</option>
            <option value="trebuchet_ms">Trebuchet MS</option>
            <option value="verdana">Verdana</option>
          </select>
        )}
        <button className="ql-bold" />
        <button className="ql-italic" />
        <button className="ql-underline" />
        <select className="ql-align" />
        <select className="ql-color" />
        <select className="ql-background" />
        <button className="ql-link" />
        <select className="ql-size">
          <option value="10px">10px</option>
          <option value="12px">12px</option>
          <option value="14px">14px</option>
          <option value="16px">16px</option>
          <option value="18px">18px</option>
          <option value="20px">20px</option>
          <option value="22px">22px</option>
          <option value="24px">24px</option>
        </select>
      </div>
      <ReactQuill
        theme="snow"
        ref={ref}
        defaultValue={value}
        placeholder="本文を記入してください"
        onChange={(content, delta, source, editor) => {
          onChange(convertUrlToLink(content), editor.getText());
        }}
        modules={{
          ...modules,
          toolbar: {
            container: '#toolbar',
          },
        }}
        bounds={'#quill-container'}
        onBlur={() => {
          onChange(convertUrlToLink(value));
        }}
        formats={[
          'link',
          'bold',
          'italic',
          'underline',
          'size',
          'align',
          'color',
          'header',
          'background',
          'font',
        ]}
      />
      <div id="quill-container" />
      <Actions>
        <Code
          mailCampaign={mailCampaign}
          onSelect={(value: string) => {
            if (ref.current?.editor.getSelection()) {
              ref.current?.editor.insertText(
                ref.current?.editor.getSelection().index,
                value,
              );
            }
          }}
        />
      </Actions>
    </Container>
  );
};

const Container = styled.div`
  padding: 8px 8px 40px 8px;
  border: solid 1px #e1e6eb;
  position: relative;
  border-radius: 4px;
  z-index: 5;

  .ql-snow .ql-picker.ql-size .ql-picker-label::before,
  .ql-snow .ql-picker.ql-size .ql-picker-item::before {
    content: attr(data-value);
  }

  .ql-snow .ql-picker.ql-size {
    width: 50px;

    .ql-picker-label {
      padding-left: 0;
    }

    .ql-picker-options {
      width: 50px;
    }
  }

  .ql-snow .ql-picker.ql-header {
    width: 90px;
  }

  .ql-container.ql-snow {
    min-height: 150px;
    border: none;

    .ql-editor {
      min-height: 200px;
      overflow-y: scroll;
      resize: vertical;
      padding: 0;
      font-family:
        monospace,
        -apple-system,
        sans-serif;

      &::before {
        left: 0;
      }
    }
  }

  .ql-toolbar {
    position: absolute;
    bottom: 40px;
    width: 100%;
    transform: translateY(100%);
    border: none;
    background: transparent;
  }

  #quill-container {
    /* reset */
    margin: 0 auto;
    font-size: 14px;
    line-height: 1.5;
    padding: 0;
    font-family:
      monospace,
      -apple-system,
      sans-serif;

    div,
    dl,
    dt,
    dd,
    ul,
    ol,
    li,
    h1,
    h2,
    h3,
    h4,
    h5,
    h6,
    pre,
    code,
    form,
    fieldset,
    legend,
    input,
    textarea,
    p,
    blockquote,
    th,
    td {
      margin: 0;
      padding: 0;
    }

    table {
      width: 100%;
    }

    table,
    td {
      border-collapse: collapse;
      border-collapse: collapse;
      border-spacing: 0;
    }

    img {
      border: 0 none;
      height: auto;
      line-height: 100%;
      outline: none;
      text-decoration: none;
    }

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      font-size: 100%;
      font-weight: normal;
    }

    h1 {
      font-size: 2em;
    }

    h2 {
      font-size: 1.5em;
    }

    h3 {
      font-size: 1.17em;
    }

    h4 {
      font-size: 1em;
    }

    h5 {
      font-size: 0.83em;
    }

    h6 {
      font-size: 0.67em;
    }

    a {
      text-decoration: underline;
    }

    a img {
      border: 0 none;
    }

    .ql-align-center {
      text-align: center;
    }

    .ql-align-right {
      text-align: right;
    }
  }
`;

const Actions = styled.div`
  position: absolute;
  right: 8px;
  bottom: 3rem;
  display: flex;
  gap: 8px;
`;
